import { useQuery } from '@tanstack/react-query'
import { AutocompleteInputChangeReason } from '@mui/material/Autocomplete'
import { SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react'
import { STALE_TIME } from 'api/autocomplete'
import MultiSelectField, { IMultiSelectProps } from 'components/form/MultiSelectField'
import { useDebounce } from 'hooks/useDebounce'
import { DropDownOptions } from 'types/common'
import {
  AutocompleteFilterSource,
  fetchAutocompleteFilterSource,
  isSameAutocompleteFilterValue,
  toAutocompleteFilterOptions,
} from './autocompleteFilterSource'

type AutocompleteFilterFieldProps = IMultiSelectProps & {
  source: AutocompleteFilterSource
  debounceTime?: number
}

/** Dropdown filter that searches its autocomplete source server-side with a debounced `q`. */
export const AutocompleteFilterField = (props: AutocompleteFilterFieldProps) => {
  const { source, debounceTime = 500, options: knownOptions, value: incomingValue, ...multiSelectProps } = props
  // The table rebuilds the selected option whenever a search registers new items. MUI compares `value` by
  // reference and would write the selected label back into the input while the user is typing.
  const stableValue = useRef(incomingValue)
  if (!isSameAutocompleteFilterValue(stableValue.current, incomingValue)) {
    stableValue.current = incomingValue
  }
  const value = stableValue.current
  const [inputValue, setInputValue] = useState('')
  const searchValue = useDebounce(inputValue.trim(), debounceTime)

  const { data, isFetching } = useQuery({
    queryKey: source.queryKey(searchValue),
    queryFn: () => fetchAutocompleteFilterSource(source, searchValue),
    enabled: source.enabled ?? true,
    keepPreviousData: true,
    staleTime: STALE_TIME,
  })

  const options = useMemo(() => {
    if (!data) return knownOptions
    const results = toAutocompleteFilterOptions(data.data, source)
    // The selected option stays available so the field can display it while other results are listed.
    const selectedOption = value as unknown as DropDownOptions | string | string[] | undefined
    const selected =
      selectedOption && typeof selectedOption === 'object' && !Array.isArray(selectedOption) ? [selectedOption] : []
    const missing = selected.filter((option) => !results.some((result) => result.value === option.value))
    return [...results, ...missing]
  }, [data, knownOptions, source, value])

  const handleInputChange = useCallback(
    (_event: SyntheticEvent, newValue: string, reason: AutocompleteInputChangeReason) => {
      // A 'reset' with text is MUI echoing the selected label into the input, not a user search.
      if (reason === 'reset' && newValue !== '') return
      setInputValue(newValue)
    },
    []
  )

  return (
    <MultiSelectField
      {...multiSelectProps}
      value={value}
      options={options}
      loading={isFetching}
      onInputChange={handleInputChange}
      onClose={(e) => {
        setInputValue('')
        multiSelectProps.onClose?.(e)
      }}
    />
  )
}
